import React, { useCallback } from 'react';
import { useStoreValue } from 'store';
import { Button, Container, Grid, Message, Text, RadioTile, Stack } from 'basis';
import { submitHandler } from 'utils';
import { STEP_PROCESSING, STEP_PROCESSING_BIOMETRICS } from '_config';
import { LayoutPage, LayoutContent, Errors, Form, SaveAndCompleteLaterButton, YellowMessage } from 'components';
import { useHistory } from 'react-router-dom';
import { useUpdateBioConsent, useDataLayerAndAnalyticsLinks } from 'hooks';

export const FacialVerificationConsent = () => {
  const history = useHistory();
  const [storeState, updateStore] = useStoreValue();
  const { trackEventSender } = useDataLayerAndAnalyticsLinks(storeState);
  const { sendRequest, loading } = useUpdateBioConsent();
  const onSubmit = useCallback(
    ({ values, errors }) => {
      const postAndRedirect = async () => {
        const result = await sendRequest({ facialBiometricsConsent: values.facialBiometricsConsent === 'yes' });
        let nextStep = STEP_PROCESSING;
        if (values.facialBiometricsConsent === 'yes') {
          nextStep = STEP_PROCESSING_BIOMETRICS;
        }
        updateStore({
          applicationErrors: null,
          applicationStatus: result.status,
          activeStep: nextStep,
        });
        history.push(nextStep);
      };

      trackEventSender(
        {
          category: 'application',
          action: 'application-navigation',
          location: 'facial-biometrics-consent',
          label: 'Next',
        },
        { applicationStatus: storeState.applicationStatus },
      );

      submitHandler({ submit: postAndRedirect, errors });
    },
    [history, sendRequest, storeState.applicationStatus, trackEventSender, updateStore],
  );

  if (storeState.applicationErrors) {
    return (
      <LayoutPage>
        <Errors />
      </LayoutPage>
    );
  }

  return (
    <LayoutPage>
      <Container margin="0 0 13 0" hasBreakpointWidth>
        <Form
          id="bioConsent"
          key="bioConsent"
          initialValues={{ facialBiometricsConsent: true }}
          onSubmit={onSubmit}
          submitButtonLabel="Next"
          showSaveDraftButton
          saveDraftOnSubmit
          saveDraftOnSessionTimeout
          disableFormCache
          hideFormButtons
        >
          {formData => {
            return (
              <>
                <LayoutContent rowsGap="0" noSessionTimeout>
                  <Stack gap="8">
                    <Container>
                      <Text>
                        To verify your identity online, we use facial verification. We’ve partnered with IDVerse to make
                        this process simple and secure.
                      </Text>
                    </Container>
                    <RadioTile
                      name="facialBiometricsConsent"
                      label="How would you like to verify your identity?"
                      options={[
                        {
                          label: 'I consent to facial verification checks.',
                          value: 'yes',
                        },
                        {
                          label: "I don't consent to facial verification checks.",
                          value: 'no',
                        },
                      ]}
                      value={formData.facialBiometricsConsent}
                      showCircles
                      columns="1"
                      color="white"
                      testId="bio-consent"
                    />
                    {formData.state.values.facialBiometricsConsent === 'no' && (
                      <Container margin="0" testId="bio-consent-opt-out-warning">
                        <YellowMessage>
                          <Message testId="yellow-message" severity="warning-or-significant">
                            <Text>You may need to upload certified copies and may take up to 2 business days.</Text>
                          </Message>
                        </YellowMessage>
                      </Container>
                    )}
                  </Stack>
                  <Grid rowsGap="4" colsGap="10" cols="2" margin="8 0 0 0">
                    <Grid.Item colSpan="all" colSpan-sm="0" alignItems="center">
                      <Grid>
                        <Button width="100%" type="submit" loading={loading}>
                          Next
                        </Button>
                      </Grid>
                    </Grid.Item>
                    <Grid.Item colSpan="all" rowSpan="1" colSpan-sm="1" rowSpan-sm="0">
                      <Grid>
                        <SaveAndCompleteLaterButton />
                      </Grid>
                    </Grid.Item>
                  </Grid>
                </LayoutContent>
              </>
            );
          }}
        </Form>
      </Container>
    </LayoutPage>
  );
};
